import React, { useEffect, useState } from 'react';
import { getToken } from '../../functions/app_functions.js';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    CardElement,
    PaymentRequestButtonElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import { trackEvent } from "../../utils/analytics";

const stripe_api_key = process.env.REACT_APP_STRIPE_API_KEY || process.env.STRIPE_API_KEY || null;
const stripePromise = stripe_api_key ? loadStripe(stripe_api_key) : Promise.resolve(null);

function CheckoutForm(props) {
    const stripe = useStripe();
    const elements = useElements();
    const [status, setStatus] = useState('');
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [walletReady, setWalletReady] = useState(false);

    const trackPaymentStart = (checkout_channel) => {
        trackEvent("trip_purchase_payment_start", {
            trip_id: props.trip_id,
            user_id_action: props.user_id,
            checkout_channel: checkout_channel
        });
    };

    const trackPaymentSuccess = (checkout_channel) => {
        trackEvent("trip_purchase_payment_success", {
            trip_id: props.trip_id,
            user_id_action: props.user_id,
            checkout_channel: checkout_channel
        });
    };

    const trackPaymentError = (checkout_channel) => {
        trackEvent("trip_purchase_payment_error", {
            trip_id: props.trip_id,
            user_id_action: props.user_id,
            checkout_channel: checkout_channel
        });
    };

    const setPaymentLoading = () => {
        props.setLoading("stripe_payment");
        setStatus('');
    };

    const unsetPaymentLoading = () => {
        props.unsetLoading("stripe_payment");
    };

    const createPaymentIntent = async (checkout_channel, wallet_hint) => {
        let response = await fetch(props.baseUrl + "/createpaymentintent", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getToken()}`
            },
            body: JSON.stringify({
                amount: props.amount,   // backend should still verify this server-side
                currency: "usd",
                user_id: props.user_id,
                trip_id: props.trip_id,
                trip_title: props.trip_title,
                owner_name: props.owner_name,
                owner_id: props.owner_id,
                items: props.items,
                checkout_channel: checkout_channel || "card",
                wallet_hint: wallet_hint || null
            })
        });

        response = await response.json();
        const result = response.value;
        if (!result || !result.client_secret) {
            throw new Error(response?.error || result?.error || "Unable to initialize payment");
        }
        return result;
    };

    const handlePaymentSuccess = async (checkout_channel) => {
        setStatus("Payment successful!");
        trackPaymentSuccess(checkout_channel);
        await props.handlePurchase();
        unsetPaymentLoading();
    };

    const handlePaymentError = (errorMessage, checkout_channel) => {
        const message = errorMessage || "Technical error";
        setStatus(`Payment failed: ${message}`);
        props.setError("Payment failed", message);
        trackPaymentError(checkout_channel);
        unsetPaymentLoading();
    };

    const handlePaymentStatus = (paymentIntent, checkout_channel) => {
        setStatus(`Payment status: ${paymentIntent?.status || "unknown"}`);
        trackPaymentError(checkout_channel);
        unsetPaymentLoading();
    };

    useEffect(() => {
        let mounted = true;

        if (!stripe) {
            setPaymentRequest(null);
            setWalletReady(false);
            return () => {
                mounted = false;
            };
        }

        const amountInCents = parseInt(props.amount, 10) * 100;
        if (!amountInCents || amountInCents <= 0) {
            setPaymentRequest(null);
            setWalletReady(false);
            return () => {
                mounted = false;
            };
        }

        const request = stripe.paymentRequest({
            country: "US",
            currency: "usd",
            total: {
                label: props.trip_title || "Trip itinerary",
                amount: amountInCents
            },
            requestPayerName: true,
            requestPayerEmail: true
        });

        request.canMakePayment().then((result) => {
            if (!mounted) return;
            if (result) {
                setPaymentRequest(request);
                setWalletReady(true);
            } else {
                setPaymentRequest(null);
                setWalletReady(false);
            }
        }).catch(() => {
            if (!mounted) return;
            setPaymentRequest(null);
            setWalletReady(false);
        });

        return () => {
            mounted = false;
        };
    }, [stripe, props.amount, props.trip_title]);

    useEffect(() => {
        if (!paymentRequest || !stripe) return;

        const handleWalletPayment = async (event) => {
            const wallet_hint = event?.paymentMethod?.card?.wallet?.type || null;
            const checkout_channel = wallet_hint === "apple_pay" || wallet_hint === "google_pay"
                ? wallet_hint
                : "wallet";
            let paymentRequestCompleted = false;

            const completePaymentRequest = (status) => {
                if (paymentRequestCompleted) return;
                event.complete(status);
                paymentRequestCompleted = true;
            };

            trackPaymentStart(checkout_channel);
            setPaymentLoading();

            try {
                const result = await createPaymentIntent(checkout_channel, wallet_hint);

                const firstConfirmResult = await stripe.confirmCardPayment(
                    result.client_secret,
                    {
                        payment_method: event.paymentMethod.id
                    },
                    {
                        handleActions: false
                    }
                );

                if (firstConfirmResult.error) {
                    completePaymentRequest("fail");
                    handlePaymentError(firstConfirmResult.error.message, checkout_channel);
                    return;
                }

                if (firstConfirmResult.paymentIntent && firstConfirmResult.paymentIntent.status === "requires_action") {
                    const actionConfirmResult = await stripe.confirmCardPayment(result.client_secret);
                    if (actionConfirmResult.error) {
                        completePaymentRequest("fail");
                        handlePaymentError(actionConfirmResult.error.message, checkout_channel);
                        return;
                    }

                    if (actionConfirmResult.paymentIntent && actionConfirmResult.paymentIntent.status === "succeeded") {
                        completePaymentRequest("success");
                        await handlePaymentSuccess(checkout_channel);
                        return;
                    }

                    completePaymentRequest("fail");
                    handlePaymentStatus(actionConfirmResult.paymentIntent, checkout_channel);
                    return;
                }

                if (firstConfirmResult.paymentIntent && firstConfirmResult.paymentIntent.status === "succeeded") {
                    completePaymentRequest("success");
                    await handlePaymentSuccess(checkout_channel);
                    return;
                }

                completePaymentRequest("fail");
                handlePaymentStatus(firstConfirmResult.paymentIntent, checkout_channel);
            } catch (err) {
                completePaymentRequest("fail");
                handlePaymentError(err.message || "Technical error", checkout_channel);
            }
        };

        paymentRequest.on("paymentmethod", handleWalletPayment);

        return () => {
            if (paymentRequest.off) {
                paymentRequest.off("paymentmethod", handleWalletPayment);
            }
        };
    }, [paymentRequest, stripe, props.amount, props.user_id, props.trip_id, props.trip_title, props.owner_id, props.owner_name, props.items, props.cc_name]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        trackPaymentStart("card");

        if (!stripe || !elements) return;

        // Set laoding state
        setPaymentLoading();

        try {
            // 1) Ask backend to create a PaymentIntent
            const result = await createPaymentIntent("card", null);

            // 2) Confirm the PaymentIntent with Stripe.js
            const card = elements.getElement(CardElement);
            if (!card) {
                throw new Error("Card data is not available");
            }

            const paymentMethod = {
                card: card
            };
            if (props.cc_name && props.cc_name.trim().length > 0) {
                paymentMethod.billing_details = {
                    name: props.cc_name.trim()
                };
            }

            const { error, paymentIntent } = await stripe.confirmCardPayment(
                result.client_secret,
                {
                    payment_method: paymentMethod
                }
            );

            if (paymentIntent && paymentIntent.status === "succeeded") {
                await handlePaymentSuccess("card");
                return;
            }
            else if (error) {
                handlePaymentError(error.message, "card");
                return;
            }

            handlePaymentStatus(paymentIntent, "card");

        } catch (err) {
            handlePaymentError(err.message || "Technical error", "card");
        }
    };


    return (
        <form onSubmit={handleSubmit}>
            <div className="trip__popup__element trip__popup__element--stripe-element">
                <CardElement
                    options={{
                        style: {
                            base: {
                                color: '#331317',
                                fontFamily: 'arial',
                                fontSize: '16px',
                                '::placeholder': {
                                    color: '#CECACB',
                                }
                            },
                            invalid: {
                                color: '#aa5375',
                                iconColor: '#aa5375',
                            },
                        },
                    }}
                />
            </div>

            {walletReady && paymentRequest &&
                <div className="trip__popup__element trip__popup__element--stripe-element">
                    <PaymentRequestButtonElement
                        options={{
                            paymentRequest: paymentRequest,
                            style: {
                                paymentRequestButton: {
                                    type: "default",
                                    theme: "dark",
                                    height: "44px"
                                }
                            }
                        }}
                    />
                </div>
            }

            {status &&
                <div className={"text-small" + (status.includes("Payment successful!") ? " text-color-primary" : " text-color-tertiary")}>
                    {status}
                </div>
            }

            <div className="trip__popup__element trip__popup__buttonbox">
                {status !== "Payment successful!" &&
                    <button className={"button button-primary"} type="submit" disabled={!stripe}>
                        Pay
                    </button>
                }
            </div>
        </form >
    );
}

export default function StripePayment(props) {
    if (!stripe_api_key) {
        return (
            <div className="text-small text-color-tertiary">
                Stripe is not configured. Set REACT_APP_STRIPE_API_KEY in your webapp environment.
            </div>
        );
    }

    return (
        <Elements stripe={stripePromise}>
            <CheckoutForm {...props} />
        </Elements>
    );
}
